/**
 * PendencyBlock — §28 ADR-109 canonical block.
 *
 * Mini-cards canônicos do painel direito (Brief 2A r7b `.mini-card`):
 * head (relógio + título + LifecycleBadge + etiqueta de QUEM prometeu) + excerpt
 * opcional + meta (responsável · prazo · prioridade · proveniência) + ações
 * Resolver / Passar a / Cancelar.
 *
 * **Quem prometeu é uma etiqueta, não uma segunda tabela** (spec
 * `2026-08-14-aba-acoes-conversa-em-curso-design.md` §5.3). A Pendency é a casa única
 * do compromisso — do operador, do cliente, ou anotado à mão — e `provenanceOf`
 * traduz `source`+`aiAgentId` no rótulo. O componente nunca interpreta `source`.
 *
 * **«Passar a»** usa `useHandoffTargets` — a MESMA fonte que o atendente já usa para
 * transferir conversa. Não `useUsers`: aquele é gated por `users-read`, que é
 * capability de SUPERVISOR, e o atendente (usuário principal desta tela) não a tem —
 * o seletor nasceria vazio justamente para quem mais precisa dele. Sem
 * `peer-coordination-handoff` o botão não renderiza, em vez de renderizar cinza.
 */

import { provenanceOf, type Provenance } from '@lume/shared';
import { Calendar, Clock, Sparkles, User } from 'lucide-react';
import { useState } from 'react';

import { useCapability } from '../../../../hooks/use-capability';
import { useHandoffTargets } from '../../../../hooks/use-handoffs';
import { useResolvePendency, useUpdatePendency } from '../../../../hooks/use-pendencies';

import { LifecycleBadge } from './LifecycleBadge';

import type { PendencyFixture } from '../../hooks/use-panel-fixtures';

export function PendencyBlock({
  items,
  disabled = false,
}: {
  items: PendencyFixture[];
  /** §2.8 — em conversa terminal todas as ações ficam disabled (read-only). */
  disabled?: boolean;
}): JSX.Element {
  const resolvePendency = useResolvePendency();
  const updatePendency = useUpdatePendency();
  const canDelegate = useCapability('peer-coordination-handoff');
  const [delegating, setDelegating] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="empty-mini">
        <Clock className="icon" size={14} aria-hidden />
        Nenhuma pendência aberta nesta conversa.
      </div>
    );
  }

  return (
    <>
      {items.map((p) => {
        const prov = provenanceOf(p.source, p.aiAgentId);
        const isActionable = p.state === 'open' || p.state === 'in-progress-pendency';

        return (
          <article key={p.id} className="mini-card" data-testid={`pendency-${p.id}`}>
            <div className="mini-card__head">
              <Clock className="icon" size={14} aria-hidden />
              <span className="mini-card__title">{p.title}</span>
              {/* Selo de estado só quando ele DIZ algo. `open` é o padrão — estar na
                  lista já significa que está aberta —, e em 360px o selo redundante
                  empurrava o título para quatro linhas ao lado da etiqueta de quem
                  prometeu. Medido na validação visual de 14/Ago. */}
              {p.state !== 'open' && <LifecycleBadge state={p.state} />}
              <WhoPill provenance={prov} pendencyId={p.id} />
            </div>

            {p.excerpt !== undefined && <p className="mini-card__excerpt">{p.excerpt}</p>}

            <div className="mini-card__meta">
              <User className="icon" size={14} aria-hidden /> {p.assignedTo}
              <span className="bullet-divider" aria-hidden />
              <Calendar className="icon" size={14} aria-hidden />
              {/* Sem prazo o card escrevia «até » e parava — a palavra sozinha, sem
                  data. Dizer «sem prazo» é honesto E acionável: é a pendência que
                  não vai cobrar ninguém. */}
              {p.dueAt.length > 0 ? `até ${p.dueAt}` : 'sem prazo'}
              {/* Prioridade só quando ela informa. `medium` é o default que o registro
                  inline nem pergunta; pintá-lo de laranja transformava o normal em
                  alerta, e alerta que aparece sempre para de ser alerta. */}
              {p.priority === 'high' && (
                <>
                  <span className="bullet-divider" aria-hidden />
                  <span style={{ color: 'var(--danger-fg)' }}>{priorityLabel(p.priority)}</span>
                </>
              )}
              {prov.originLabel !== null && (
                <>
                  <span className="bullet-divider" aria-hidden />
                  <span
                    style={{
                      color: 'var(--ai-fg)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Sparkles size={10} aria-hidden /> {prov.originLabel}
                  </span>
                </>
              )}
            </div>

            {isActionable && (
              <div className="mini-card__actions">
                <button
                  type="button"
                  className="btn btn--primary btn--xs"
                  onClick={() =>
                    resolvePendency.mutate({ pendencyId: p.id, expectedVersion: p.version })
                  }
                  disabled={disabled || resolvePendency.isPending}
                >
                  ✓ Resolver
                </button>
                {canDelegate && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--xs"
                    onClick={() => setDelegating((cur) => (cur === p.id ? null : p.id))}
                    disabled={disabled}
                  >
                    Passar a
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--ghost btn--xs"
                  onClick={() =>
                    updatePendency.mutate({
                      pendencyId: p.id,
                      expectedVersion: p.version,
                      body: { status: 'cancelled' },
                    })
                  }
                  disabled={disabled || updatePendency.isPending}
                >
                  Cancelar
                </button>
              </div>
            )}

            {delegating === p.id && (
              <DelegateRow
                disabled={disabled || updatePendency.isPending}
                onConfirm={(userId) => {
                  updatePendency.mutate({
                    pendencyId: p.id,
                    expectedVersion: p.version,
                    body: { delegatedToUserId: userId },
                  });
                  setDelegating(null);
                }}
                onCancel={() => setDelegating(null)}
              />
            )}
          </article>
        );
      })}
    </>
  );
}

/** Etiqueta de quem prometeu — reusa `.lifecycle-pill` do DS, com tom por origem. */
function WhoPill({
  provenance,
  pendencyId,
}: {
  provenance: Provenance;
  pendencyId: string;
}): JSX.Element {
  const tone =
    provenance.who === 'client'
      ? { background: 'var(--ai-bg)', color: 'var(--ai-fg)', borderColor: 'var(--ai-border)' }
      : provenance.who === 'me'
        ? {
            background: 'var(--bg-surface-2)',
            color: 'var(--fg-2)',
            borderColor: 'var(--border-default)',
          }
        : { color: 'var(--fg-4)', borderColor: 'var(--border-subtle)' };

  return (
    <span className="lifecycle-pill" style={tone} data-testid={`pendency-who-${pendencyId}`}>
      {provenance.whoLabel}
    </span>
  );
}

/**
 * Linha inline de delegação. Sem modal — é o mesmo idioma do resto da aba: a ação
 * acontece no próprio cartão. Confirmar fica travado enquanto ninguém foi escolhido,
 * porque delegar para o vazio limparia o `delegatedToUserId` em silêncio.
 */
function DelegateRow({
  disabled,
  onConfirm,
  onCancel,
}: {
  disabled: boolean;
  onConfirm: (userId: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const targets = useHandoffTargets();
  const [userId, setUserId] = useState('');

  return (
    <div className="mini-card__actions" style={{ alignItems: 'center' }}>
      <select
        aria-label="Passar para"
        className="btn btn--ghost btn--xs"
        style={{ flex: 1, minWidth: 0 }}
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        disabled={disabled || targets.isLoading}
      >
        <option value="">{targets.isLoading ? 'Carregando…' : 'escolha um colega'}</option>
        {targets.data.map((t) => (
          <option key={t.userId} value={t.userId}>
            {t.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn--primary btn--xs"
        onClick={() => onConfirm(userId)}
        disabled={disabled || userId.length === 0}
      >
        Confirmar
      </button>
      {/* «Voltar», não «Cancelar»: o cartão já tem um Cancelar logo acima, que
          CANCELA A PENDÊNCIA. Dois botões com o mesmo nome e efeitos opostos no
          mesmo cartão é armadilha — aqui só se fecha a linha de delegação. */}
      <button type="button" className="btn btn--ghost btn--xs" onClick={onCancel}>
        Voltar
      </button>
    </div>
  );
}

function priorityLabel(p: 'low' | 'medium' | 'high'): string {
  if (p === 'high') return 'alta prioridade';
  if (p === 'medium') return 'média prioridade';
  return 'baixa prioridade';
}
